'use client';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type React from 'react';
import Like from '@/features/Like/Like';
import { useApp } from '@/forApp/states/appState/appState';
import type { BaseEntityT } from '@/forApp/types/Entity';
import IconButton from '@/shared/Buttons/IconButton/IconButton';
import { Icons } from '@/shared/Icons/Icons';
import ImageWithLoader from '@/shared/Image/ImageWithLoader';
import Prices from '@/shared/Prices';
import { getRouteHref, ROUTES } from '@/forApp/utils/routes';

import styles from './styles.module.css';

type ProfileObjectCardProps = {
  card: BaseEntityT;
  type: 'promo' | 'favorite';
  mutate?: () => void;
  profile?: {
    role: string;
  };
  typeObj: string;
};

const SmallCard: React.FC<ProfileObjectCardProps> = ({
  card,
  type,
  mutate,
  profile = { role: 'user' },
  typeObj,
}) => {
  const router = useRouter();
  const { addModal } = useApp();
  const goToEdit = (slug: string, type: string) => {
    router.push(`/profile/edit/${type}/${slug}`);
  };

  const onArchive = () => {
    addModal({
      name: 'ArchiveObjectModal',
      object: card,
      mutate,
    });
  };

  const onDelete = () => {
    addModal({
      name: 'DeleteObjectModal',
      object: card,
      mutate,
    });
  };

  const cardThumbnail = card?.images?.length
    ? card.images[0]?.thumbnail_path || '/imgs/no-photo.png'
    : '/imgs/no-photo.png';

  return (
    <Link
      href={getRouteHref(ROUTES.ENTITY, { type: typeObj, slug: card?.slug })}
      className={styles.profileObjectItem}
    >
      <div
        className={
          type === 'promo'
            ? styles.profileObjectItemImage
            : styles.profileObjectItemImage
        }
      >
        {type === 'promo' && card.status === 'in_moderation' && (
          <div className={styles.badge}>На модерации</div>
        )}
        {type === 'promo' && card.status === 'archive' && (
          <div className={styles.badge}>В архиве</div>
        )}
        <ImageWithLoader
          src={cardThumbnail ? cardThumbnail : '/imgs/no-photo.png'}
          alt="Фото объекта"
          width={175}
          height={175}
          objectFit={'cover'}
        />
      </div>
      <div className={styles.profileObjectItemContent}>
        <div>
          <Prices
            price={card?.price}
            priceMeter={card?.price_meter}
            totalArea={card?.total_area}
          />
          <div className={styles.profileObjectItemAction}>
            {type === 'promo' ? (
              profile.role !== 'admin_agency' && (
                <div className={styles.iconButtonsContainer}>
                  <IconButton
                    icon={<Icons.Pencil />}
                    onClick={() => {
                      goToEdit(card.slug, card.entity_type.slug);
                    }}
                    className={styles.editButton}
                    ariaLabel="Edit"
                    tooltip={'Редактировать'}
                    tooltipPosition="top"
                  />
                  {card.status !== 'archive' && (
                    <IconButton
                      icon={<Icons.ArrowArchive />}
                      onClick={onArchive}
                      className={styles.archiveButton}
                      ariaLabel="Archive"
                      tooltip={'Добавить в архив'}
                      tooltipPosition="top"
                    />
                  )}
                  <IconButton
                    icon={<Icons.Close />}
                    onClick={onDelete}
                    className={styles.deleteButton}
                    ariaLabel="Delete"
                    tooltip={'Удалить'}
                    tooltipPosition="top"
                  />
                </div>
              )
            ) : (
              <Like entity_id={card?.id} />
            )}
          </div>
        </div>
        <span className={styles.address}>{card?.full_address}</span>
      </div>
    </Link>
  );
};

export default SmallCard;
